import {
  ParameterStructuredTable,
  ParameterStructuredTableMetadata,
  ParameterStructuredTableRowMetadata,
} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import {ParameterStructuredTableModeToTypeName} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import isTableConfig from 'notebook/structured-tables/util/isTableConfig';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createDebuggableSelector, createDeepSelector} from 'notebook/tables/store/selectors/SelectorCreators';
import {
  createTableEditableStoreStructuredSelector,
  TableEditableStore,
} from 'notebook/tables/store/TableStore';
import {
  ColumnConstraint,
  Row,
  RowData,
  RowProperties,
  StructuredTableToTableMetadata,
  Table,
} from 'notebook/tables/TableConstants';
import APIUtil from 'util/APIUtil';

/**
 * This holds volatile values of the model, or values that frequently changes due to evaluation or renders.
 * This should contain volatile values from the TableModel that are only relevant to this single row.
 *
 * Everything inside here will be deep-compare memoized.
 */
export interface VolatileRowModel {
  row: Row;
  evaluatedRow: Array<EvaluatedCell>;
  rowData: RowData | null;
  columnConstraints: Array<ColumnConstraint | null>;
}

/**
 * This model is what is passed down to the components, as well as to the CellModel.
 * This can contain data that is derived from the TableModel or TableEditableStore
 */
export interface RowModel extends VolatileRowModel {
  rowProperties: RowProperties;
}

const isConfigTable = (tableModel: TableModel): boolean => {
  const metadata = tableModel.props.table.metadata as StructuredTableToTableMetadata<unknown>;
  return (
    metadata?.isConfig ||
    isTableConfig(tableModel.props.table._tableType) ||
    Boolean(tableModel.props.table.fileId && APIUtil.isConfigurationDocument(tableModel.props.table.fileId))
  );
};

export const isTableRowTombstoned = (table: Table, rowIndex: number): boolean | undefined =>
  (table.rowData?.[rowIndex]?.rowMetadata as ParameterStructuredTableRowMetadata)?.isTombstoned;

export const getTombstoneReasonTooltip = (table: Table): string => {
  const tableMetadata = table.metadata as StructuredTableToTableMetadata<ParameterStructuredTable>;
  const tableMode = (tableMetadata?.metadata as unknown as ParameterStructuredTableMetadata)?.mode;
  const typeName = ParameterStructuredTableModeToTypeName[tableMode] || 'row';
  return `Deleted ${typeName}. Please adjust any formulas before deleting row.`;
};

// This logic is temporarily duplicated from TableEditable while redux is still behind a feature flag
// Any changes to this logic should be reflected in TableEditable._getRowProperties
function createRowPropertiesSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  rowIndex: number
): (store: TableEditableStore) => RowProperties {
  const selectTableRowProperties = createTableEditableStoreStructuredSelector(
    {
      hasErrors: (store) => tableSelector(store).props.errorRows?.[rowIndex] === true,
      hasWarnings: (store) => tableSelector(store).props.warningRows?.[rowIndex] === true,
      hasUnsubmittedChanges: (store) => {
        const tableModel = tableSelector(store);
        return (
          tableModel.props.table.rowData?.[rowIndex]?.uiData?.hasUnsubmittedChanges &&
          !isConfigTable(tableModel)
        );
      },
      isOutdated: (store) => {
        const tableModel = tableSelector(store);
        return (
          tableModel.props.staleRows != null &&
          tableModel.props.staleRows.length > 0 &&
          tableModel.props.staleRows.includes(rowIndex)
        );
      },
      archivePurpose: (store) => tableSelector(store).props.table.rowData?.[rowIndex]?.uiData?.archivePurpose,
      isTombstoned: (store) => {
        const tableModel = tableSelector(store);
        return isTableRowTombstoned(tableModel.props.table, rowIndex);
      },
      tombstoneReasonTooltip: (store) => {
        const tableModel = tableSelector(store);
        const isTombstoned = isTableRowTombstoned(tableModel.props.table, rowIndex);
        let tombstoneReasonTooltip: string | null = null;
        if (isTombstoned) {
          tombstoneReasonTooltip = getTombstoneReasonTooltip(tableModel.props.table);
        }
        return tombstoneReasonTooltip;
      },
      unsubmittedChangesTooltip: (store) =>
        tableSelector(store).props.table.rowData?.[rowIndex]?.uiData?.unsubmittedChangesTooltip,
    },
    createDebuggableSelector.withTypes<TableEditableStore>()
  );

  return selectTableRowProperties;
}

function createVolatileRowModelSelector(
  rowIndex: number,
  tableSelector: (store: TableEditableStore) => TableModel
): (store: TableEditableStore) => VolatileRowModel {
  return createDeepSelector.withTypes<TableEditableStore>()(
    [
      // row
      (store) => tableSelector(store).props.table.table[rowIndex],
      // evaluatedRow
      (store) => tableSelector(store).props.computedTable[rowIndex],
      // rowData
      (store) => tableSelector(store).props.table.rowData?.[rowIndex] ?? null,
      // columnConstraints
      (store) => tableSelector(store).props.table.columnConstraints,
    ],
    (row, evaluatedRow, rowData, columnConstraints) => ({
      row,
      evaluatedRow,
      rowData,
      columnConstraints,
    })
  );
}

export function createRowModelSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  rowIndex: number
): (store: TableEditableStore) => RowModel {
  return createDebuggableSelector.withTypes<TableEditableStore>()(
    [
      // volatileStore
      createVolatileRowModelSelector(rowIndex, tableSelector),
      // rowProperties
      createRowPropertiesSelector(tableSelector, rowIndex),
    ],
    (volatileModel, rowProperties): RowModel => ({
      ...volatileModel,
      rowProperties,
    })
  );
}
